import { NumericKeyboard } from '@ps/sports-ui-component-library';
import type { TrackingContextValue } from '@ps/web-shared-analytics';
import { TrackingCategory, buttonClick, panelToggle } from '@ps/web-shared-analytics';
import React, { useEffect, useMemo, useRef } from 'react';
import { useField, useFormState } from 'react-final-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import type { FormData } from '../../../../types';
import { getStakeInputRegex } from '../../../../validation/getStakeInputRegex';

import { TXTCLI_Clear, TXTCLI_Undo, TXTWCL_Done } from './Keyboard.messages.json';
import styles from './Keyboard.scss';

const Keyboard: React.FC = () => {
  const hasUserOpenedKeyboard = useRef(false);
  const stakeInputDecimalSeparator = useSelector(select.betSlip.stakeInputDecimalSeparator);
  const stakeInputRegex = useMemo(
    () => getStakeInputRegex(stakeInputDecimalSeparator),
    [stakeInputDecimalSeparator]
  );
  const { active } = useFormState<FormData>({
    subscription: { active: true }
  });
  const isStakeInputField = String(active).includes('stakePerLine');
  const { input } = useField<string, HTMLInputElement>(
    String(isStakeInputField ? active : undefined)
  );
  const { formatMessage } = useIntl();
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const trackingContext: TrackingContextValue = useMemo(
    () => ({
      category: TrackingCategory.Sports,
      isMobileEmbedded
    }),
    [isMobileEmbedded]
  );

  useEffect(() => {
    if (isStakeInputField) {
      hasUserOpenedKeyboard.current = true;
    }

    /**
     * We check the user has opened the keyboard because we don't
     * want to track "false" when it initially renders.
     */
    if (hasUserOpenedKeyboard.current) {
      panelToggle(
        {
          panelName: 'Sports_Custom_Keyboard',
          isOpen: isStakeInputField
        },
        trackingContext
      );
    }
  }, [isStakeInputField, trackingContext]);

  const handleNumberPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();

    buttonClick(
      {
        buttonName: `Sports_Custom_Keyboard_${event.currentTarget.value}`
      },
      trackingContext
    );

    const newStake = `${input.value}${event.currentTarget.value}`;
    const isValid = stakeInputRegex.test(newStake);
    if (isValid) {
      input.onChange(newStake);
    }
  };

  const handleClearPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();

    buttonClick(
      {
        buttonName: 'Sports_Custom_Keyboard_Clear'
      },
      trackingContext
    );

    input.onChange('');
  };

  const handleDecimalSeparatorPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();

    buttonClick(
      {
        buttonName: 'Sports_Custom_Keyboard_DecimalSeperator'
      },
      trackingContext
    );

    const newStake = `${input.value}${stakeInputDecimalSeparator}`;
    const isValid = stakeInputRegex.test(newStake);
    if (isValid) {
      input.onChange(newStake);
    }
  };

  const handleBackspacePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();

    buttonClick(
      {
        buttonName: 'Sports_Custom_Keyboard_Undo'
      },
      trackingContext
    );

    if (input.value) {
      input.onChange(`${input.value}`.slice(0, -1));
    }
  };

  const handleDonePointerDown = () => {
    buttonClick(
      {
        buttonName: 'Sports_Custom_Keyboard_Done'
      },
      trackingContext
    );

    input.onBlur();
  };

  /**
   * Fixes an issue for browsers on iOS, which causes the keyboard to close
   * when double clicking or clicking the keyboard's container.
   * These actions result in a "mouseDown" event, losing focus on the input,
   * which in turn closes the keyboard.
   */
  const handleMouseDown = (event: React.MouseEvent<HTMLElement>) => event.preventDefault();

  return (
    <NumericKeyboard
      data-testid="bet-slip-keyboard"
      className={styles.keyboard}
      isActive={isStakeInputField}
      decimalSeparator={stakeInputDecimalSeparator}
      doneText={formatMessage(TXTWCL_Done)}
      clearText={formatMessage(TXTCLI_Clear)}
      undoText={formatMessage(TXTCLI_Undo)}
      onHandleNumberPointerDown={handleNumberPointerDown}
      onHandleClearPointerDown={handleClearPointerDown}
      onHandleDecimalSeparatorPointerDown={handleDecimalSeparatorPointerDown}
      onHandleBackspacePointerDown={handleBackspacePointerDown}
      onHandleDonePointerDown={handleDonePointerDown}
      onMouseDown={handleMouseDown}
    />
  );
};

const KeyboardWrapper: React.FC = () => {
  const isCustomKeyboardEnabled = useSelector(select.betSlip.isCustomKeyboardEnabled);

  if (!isCustomKeyboardEnabled) {
    return null;
  }

  return <Keyboard />;
};

export default KeyboardWrapper;
